import { network } from '@root/utils'
import { ExpenseClaimTag } from './expenseClaims'
import { handleReturn } from './root'

export type ExpenseCategory =
  | 'TRAVEL_TICKETS'
  | 'TAXI'
  | 'ACCOMMODATION'
  | 'MEALS_WHILE_TRAVELLING'
  | 'PARKING'
  | 'ENTERTAINMENT'
  | 'OFFICE_SUPPLIES'
  | 'SOFTWARE_AND_SUBSCRIPTIONS'
  | 'PHONE_AND_INTERNET'
  | 'TRAINING'
  | 'OTHER_TRAVEL_EXPENSE'
  | 'OTHER_EXPENSE'

export type ExpenseAnalysisStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'FAILED'

export type ExpenseVatRow = {
  id?: number
  vat_rate: string
  gross_amount: number
  vat_amount: number
}

export type ExpenseReading = {
  status: ExpenseAnalysisStatus
  merchant: string | null
  purchase_date: string | null
  amount: number | null
  suggested_category: ExpenseCategory | null
  vat_rows: ExpenseVatRow[]
}

export type ExpenseClaimExpense = {
  id: number
  trip_id: number | null
  merchant: string
  purchase_date: string | null
  description: string
  category: ExpenseCategory | null
  amount: number | null
  tags: ExpenseClaimTag[]
  vat_rows: ExpenseVatRow[]
  attachment: {
    id: number
    name: string
    type: string
    analysis_status: ExpenseAnalysisStatus
  }
  reading: ExpenseReading
}

export type ExpenseClaimExpenseWrite = {
  trip_id?: number | null
  merchant?: string
  purchase_date?: string | null
  description?: string
  category?: ExpenseCategory | null
  amount?: number | null
  tag_ids?: number[]
  vat_rows?: { vat_rate: string; gross_amount: number; vat_amount?: number }[]
}

const expensesUrl = (slug: string, claimId: number) =>
  `/v1/business/${slug}/expense_claims/${claimId}/expenses/`

export const fetchExpenseClaimExpenses = async (
  slug: string,
  claimId: number
): Promise<ExpenseClaimExpense[]> => {
  const { status, data } = await network.httpClient.request<{ results: ExpenseClaimExpense[] }>({
    url: expensesUrl(slug, claimId),
    method: 'GET',
    validateStatus: () => true
  })
  return handleReturn({ status, data }).results
}

export const fetchExpenseClaimExpense = async (
  slug: string,
  claimId: number,
  expenseId: number
): Promise<ExpenseClaimExpense> => {
  const { status, data } = await network.httpClient.request<ExpenseClaimExpense>({
    url: `${expensesUrl(slug, claimId)}${expenseId}/`,
    method: 'GET',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const createExpenseClaimExpense = async (
  slug: string,
  claimId: number,
  file: File,
  tripId?: number | null
): Promise<ExpenseClaimExpense> => {
  const body = new FormData()
  body.append('file', file)
  if (tripId) body.append('trip_id', String(tripId))
  const { status, data } = await network.httpClient.request<ExpenseClaimExpense>({
    url: expensesUrl(slug, claimId),
    method: 'POST',
    data: body,
    headers: { 'Content-Type': 'multipart/form-data' },
    validateStatus: () => true,
    timeout: 60000
  })
  return handleReturn({ status, data })
}

export const createExpenseClaimExpenseFromAttachment = async (
  slug: string,
  claimId: number,
  attachmentId: number,
  tripId?: number | null
): Promise<ExpenseClaimExpense> => {
  const { status, data } = await network.httpClient.request<ExpenseClaimExpense>({
    url: expensesUrl(slug, claimId),
    method: 'POST',
    data: {
      attachment_id: attachmentId,
      ...(tripId ? { trip_id: tripId } : {})
    },
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const updateExpenseClaimExpense = async (
  slug: string,
  claimId: number,
  expenseId: number,
  payload: ExpenseClaimExpenseWrite
): Promise<ExpenseClaimExpense> => {
  const { status, data } = await network.httpClient.request<ExpenseClaimExpense>({
    url: `${expensesUrl(slug, claimId)}${expenseId}/`,
    method: 'PATCH',
    data: payload,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const replaceExpenseClaimReceipt = async (
  slug: string,
  claimId: number,
  expenseId: number,
  file: File
): Promise<ExpenseClaimExpense> => {
  const body = new FormData()
  body.append('file', file)
  const { status, data } = await network.httpClient.request<ExpenseClaimExpense>({
    url: `${expensesUrl(slug, claimId)}${expenseId}/`,
    method: 'PATCH',
    data: body,
    headers: { 'Content-Type': 'multipart/form-data' },
    validateStatus: () => true,
    timeout: 60000
  })
  return handleReturn({ status, data })
}

export const replaceExpenseClaimReceiptFromAttachment = async (
  slug: string,
  claimId: number,
  expenseId: number,
  attachmentId: number
): Promise<ExpenseClaimExpense> => {
  const { status, data } = await network.httpClient.request<ExpenseClaimExpense>({
    url: `${expensesUrl(slug, claimId)}${expenseId}/`,
    method: 'PATCH',
    data: { attachment_id: attachmentId },
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const appendExpenseClaimReceiptPage = async (
  slug: string,
  claimId: number,
  expenseId: number,
  file: File
): Promise<ExpenseClaimExpense> => {
  const body = new FormData()
  body.append('file', file)
  const { status, data } = await network.httpClient.request<ExpenseClaimExpense>({
    url: `${expensesUrl(slug, claimId)}${expenseId}/pages/`,
    method: 'POST',
    data: body,
    headers: { 'Content-Type': 'multipart/form-data' },
    validateStatus: () => true,
    timeout: 60000
  })
  return handleReturn({ status, data })
}

export const deleteExpenseClaimExpense = async (
  slug: string,
  claimId: number,
  expenseId: number
): Promise<void> => {
  const { status, data } = await network.httpClient.request({
    url: `${expensesUrl(slug, claimId)}${expenseId}/`,
    method: 'DELETE',
    validateStatus: () => true
  })
  handleReturn({ status, data })
}

export const fetchExpenseClaimReceipt = async (
  slug: string,
  claimId: number,
  expenseId: number
): Promise<Blob> => {
  const { status, data } = await network.httpClient.request<Blob>({
    url: `${expensesUrl(slug, claimId)}${expenseId}/file/`,
    method: 'GET',
    responseType: 'blob',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}
